// CreatorMap.js
// Public Expo WEB screen.
// Uses the existing GetCreatorMap.php response.
// No database credentials or additional npm packages.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Platform,
  View,
  Text,
  Pressable,
  Linking,
} from 'react-native';

import { useIsFocused } from '@react-navigation/native';

const ORIGIN = 'https://collabbay.com';

const API =
  `${ORIGIN}/APIControllers/GetCreatorMap.php`;

const TILE_URL =
  'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

const TILE_CREDIT =
  '&copy; <a href="https://www.openstreetmap.org/copyright">' +
  'OpenStreetMap</a> contributors';

const BATCH = 12;

let leafletPromise;

/* ============================================================
 * Load map libraries in the browser
 * ============================================================ */

function asset(id, url, css = false, integrity = '') {
  return new Promise((resolve, reject) => {
    let el = document.getElementById(id);

    if (
      el?.dataset.ready === '1' ||
      (css && el?.sheet)
    ) {
      resolve();
      return;
    }

    if (el?.dataset.failed === '1') {
      el.remove();
      el = null;
    }

    const fresh = !el;

    el = el || document.createElement(
      css ? 'link' : 'script'
    );

    let timer;

    const clean = () => {
      clearTimeout(timer);
      el.removeEventListener('load', loaded);
      el.removeEventListener('error', failed);
    };

    const loaded = () => {
      el.dataset.ready = '1';
      clean();
      resolve();
    };

    const failed = () => {
      el.dataset.failed = '1';
      clean();

      reject(
        new Error(
          'The map library could not load. Please retry.'
        )
      );
    };

    el.addEventListener('load', loaded);
    el.addEventListener('error', failed);

    timer = setTimeout(failed, 16000);

    if (fresh) {
      el.id = id;

      if (css) {
        el.rel = 'stylesheet';
        el.href = url;
      } else {
        el.src = url;
        el.async = true;
      }

      if (integrity) {
        el.integrity = integrity;
        el.crossOrigin = 'anonymous';
      }

      document.head.appendChild(el);
    }
  });
}

function loadMapLibrary() {
  if (!leafletPromise) {
    leafletPromise = (async () => {
      await asset(
        'cmap-leaflet-css',
        'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css',
        true,
        'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY='
      );

      if (!window.L) {
        await asset(
          'cmap-leaflet-js',
          'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js',
          false,
          'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo='
        );
      }

      if (!window.L) {
        throw new Error(
          'The map library is unavailable.'
        );
      }

      try {
        await asset(
          'cmap-clusters-css',
          'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css',
          true
        );

        if (!window.L.markerClusterGroup) {
          await asset(
            'cmap-clusters-js',
            'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js'
          );
        }
      } catch (_) {
        // Ordinary pins remain usable without clustering.
      }

      return window.L;
    })().catch(error => {
      leafletPromise = null;
      throw error;
    });
  }

  return leafletPromise;
}

/* ============================================================
 * Data, URLs and media helpers
 * ============================================================ */

function safeUrl(value) {
  if (
    typeof value !== 'string' ||
    !value.trim()
  ) {
    return '';
  }

  try {
    const url = new URL(value.trim(), ORIGIN);

    if (
      !['https:', 'http:'].includes(url.protocol) ||
      url.username ||
      url.password
    ) {
      return '';
    }

    // Our own uploads have an HTTPS equivalent.
    // Do not rewrite unknown external hosts.
    if (
      ['collabbay.com', 'www.collabbay.com'].includes(
        url.hostname
      ) &&
      !url.port
    ) {
      url.protocol = 'https:';
    }

    return url.href;
  } catch (_) {
    return '';
  }
}

function creatorPath(name) {
  return '/creatormap' + (
    name ? '/' + encodeURIComponent(name) : ''
  );
}

function kindOf(item) {
  if (
    ['video', 'image', 'link'].includes(item?.kind)
  ) {
    return item.kind;
  }

  try {
    const url = new URL(item.url);

    if (
      /(^|\.)(instagram\.com|tiktok\.com|youtube\.com|youtu\.be|facebook\.com|fb\.watch|vimeo\.com|x\.com|twitter\.com)$/
        .test(url.hostname)
    ) {
      return 'link';
    }

    if (
      /\.(mp4|m4v|webm|mov|ogv|ogg|avi|mkv|m3u8)$/i
        .test(url.pathname)
    ) {
      return 'video';
    }

    if (
      /\.(jpe?g|png|webp|gif|avif|svg|bmp|heic|heif)$/i
        .test(url.pathname)
    ) {
      return 'image';
    }

    return 'unknown';
  } catch (_) {
    return 'link';
  }
}

function previewOf(venue) {
  return venue.media.find(
    media => kindOf(media) === 'video'
  ) || venue.media[0];
}

function posterOf(venue) {
  return (
    venue.media.find(media => media.poster)?.poster ||
    venue.media.find(
      media => kindOf(media) === 'image'
    )?.url ||
    ''
  );
}

function initials(name) {
  return String(name || 'C')
    .replace(/^@/, '')
    .slice(0, 2)
    .toUpperCase();
}

function plural(n, word) {
  return (
    `${n.toLocaleString()} ${word}` +
    `${n === 1 ? '' : 's'}`
  );
}

function dateScore(value) {
  return Date.parse(value || '') || 0;
}

function distanceKm(a, b) {
  const rad = n => n * Math.PI / 180;

  const h =
    Math.sin(rad(b.lat - a.lat) / 2) ** 2 +
    Math.cos(rad(a.lat)) *
    Math.cos(rad(b.lat)) *
    Math.sin(rad(b.lng - a.lng) / 2) ** 2;

  return 6371.0088 * 2 * Math.atan2(
    Math.sqrt(Math.min(1, h)),
    Math.sqrt(Math.max(0, 1 - h))
  );
}

function distanceLabel(km) {
  return km < 1
    ? `${Math.round(km * 1000)} m away`
    : `${km.toFixed(1)} km away`;
}

function normalise(payload) {
  if (
    !Array.isArray(payload?.venues) ||
    !Array.isArray(payload?.creators)
  ) {
    throw new Error('Unexpected API response.');
  }

  const venues = payload.venues
    .filter(venue =>
      venue && typeof venue === 'object'
    )
    .map(venue => ({
      ...venue,

      id: String(venue.id),
      name: String(venue.name || 'Venue'),

      lat: Number(venue.lat),
      lng: Number(venue.lng),

      area: String(venue.area || ''),
      city: String(venue.city || ''),
      address: String(venue.address || ''),

      booking_url: safeUrl(venue.booking_url),

      media: (
        Array.isArray(venue.media)
          ? venue.media
          : []
      )
        .filter(Boolean)
        .map(media => ({
          ...media,
          creator: String(media.creator || ''),
          url: safeUrl(media.url),
          poster: safeUrl(media.poster),

          original_url: safeUrl(
            media.original_url || media.url
          ),
        }))
        .filter(media =>
          media.url && media.creator
        ),
    }))
    .filter(venue =>
      venue.media.length &&
      Number.isFinite(venue.lat) &&
      Number.isFinite(venue.lng) &&
      Math.abs(venue.lat) <= 85.051129 &&
      Math.abs(venue.lng) <= 180
    );

  return {
    venues,

    creators: [
      ...new Set(
        payload.creators.filter(creator =>
          typeof creator === 'string' &&
          creator.trim()
        )
      ),
    ],
  };
}

/* ============================================================
 * Icons and safe external links
 * ============================================================ */

function Icon({ name, size = 19 }) {
  const paths = {
    search: (
      <>
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m16 16 5 5" />
      </>
    ),

    map: (
      <>
        <path d="m3 5 6-2 6 2 6-2v16l-6 2-6-2-6 2ZM9 3v16m6-14v16" />
      </>
    ),

    feed: (
      <>
        <rect
          x="4"
          y="3"
          width="16"
          height="7"
          rx="2"
        />

        <rect
          x="4"
          y="14"
          width="16"
          height="7"
          rx="2"
        />
      </>
    ),

    near: (
      <>
        <circle cx="12" cy="12" r="7" />
        <circle cx="12" cy="12" r="2" />
        <path d="M12 1v4m0 14v4M1 12h4m14 0h4" />
      </>
    ),

    pin: (
      <>
        <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
        <circle cx="12" cy="10" r="2.5" />
      </>
    ),

    play: (
      <path d="m9 5 10 7-10 7Z" />
    ),

    close: (
      <path d="m6 6 12 12M18 6 6 18" />
    ),

    share: (
      <path d="M12 16V3m-5 5 5-5 5 5M5 13v7h14v-7" />
    ),

    arrow: (
      <path d="M5 12h14m-6-6 6 6-6 6" />
    ),

    external: (
      <path d="M8 5h11v11M19 5 5 19" />
    ),

    fit: (
      <path d="M9 3H3v6m12-6h6v6M3 15v6h6m12-6v6h-6" />
    ),
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name] || paths.pin}
    </svg>
  );
}

function External({
  href,
  children,
  className = '',
  ...rest
}) {
  const url = safeUrl(href);

  return url ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      {...rest}
    >
      {children}
    </a>
  ) : null;
}

/* ============================================================
 * Image/video renderer
 *
 * Videos pause when offscreen, hidden, or another video plays.
 * Unknown URLs try image, then video, then an original link.
 * ============================================================ */

function Media({
  item,
  label,
  active = false,
  enabled = true,
  controls = false,
  poster = '',
}) {
  const box = useRef(null);
  const player = useRef(null);

  const [near, setNear] = useState(false);
  const [visible, setVisible] = useState(false);
  const [failed, setFailed] = useState(0);
  const [blocked, setBlocked] = useState(false);

  const type = kindOf(item);
  const url = safeUrl(item?.url);

  const effective = type === 'unknown'
    ? (failed === 0 ? 'image' : 'video')
    : type;

  const broken = type === 'unknown'
    ? failed >= 2
    : failed >= 1;

  useEffect(() => {
    setFailed(0);
    setBlocked(false);
  }, [url]);

  useEffect(() => {
    if (!box.current) {
      return undefined;
    }

    if (!window.IntersectionObserver) {
      setNear(true);
      setVisible(true);
      return undefined;
    }

    const warm = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setNear(true);
          warm.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    const watch = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting);
      },
      { threshold: 0.05 }
    );

    warm.observe(box.current);
    watch.observe(box.current);

    return () => {
      warm.disconnect();
      watch.disconnect();
    };
  }, []);

  useEffect(() => {
    const video = player.current;

    if (!video) {
      return undefined;
    }

    if (
      active &&
      enabled &&
      visible &&
      !document.hidden
    ) {
      video.muted = true;

      video.play()?.catch(() => {
        setBlocked(true);
      });
    } else {
      video.pause();
    }

    return () => video.pause();
  }, [
    active,
    enabled,
    visible,
    near,
    effective,
    broken,
    url,
  ]);

  useEffect(() => {
    const pause = () => {
      if (document.hidden) {
        player.current?.pause();
      }
    };

    document.addEventListener(
      'visibilitychange',
      pause
    );

    return () => {
      document.removeEventListener(
        'visibilitychange',
        pause
      );
    };
  }, []);

  const onPlay = event => {
    document
      .querySelectorAll('video[data-cmap-video]')
      .forEach(video => {
        if (video !== event.currentTarget) {
          video.pause();
        }
      });

    setBlocked(false);
  };

  return (
    <div className="cmap-media" ref={box}>
      {(!url || type === 'link' || broken) ? (
        <div className="cmap-media-empty">
          <Icon name="play" size={30} />

          <strong>
            {type === 'link'
              ? 'See the original post'
              : 'Preview unavailable'}
          </strong>

          <External
            href={item?.original_url || url}
          >
            Open original
            <Icon name="external" size={14} />
          </External>
        </div>
      ) : !near ? (
        <div className="cmap-media-placeholder">
          <Icon
            name={
              effective === 'video'
                ? 'play'
                : 'pin'
            }
            size={26}
          />
        </div>
      ) : effective === 'video' ? (
        <>
          <video
            ref={player}
            data-cmap-video
            src={url}
            poster={
              safeUrl(item?.poster || poster) ||
              undefined
            }
            controls={controls}
            muted
            loop
            playsInline
            preload="metadata"
            aria-label={label}
            onPlay={onPlay}
            onError={() => setFailed(n => n + 1)}
          />

          {!controls && blocked && (
            <button
              type="button"
              className="cmap-play"
              aria-label="Play preview"
              onClick={() => {
                player.current
                  ?.play()
                  ?.catch(() => setBlocked(true));
              }}
            >
              <Icon name="play" />
            </button>
          )}

          <span className="cmap-video-tag">
            <Icon name="play" size={10} />
            VIDEO
          </span>
        </>
      ) : (
        <img
          src={url}
          alt={label}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(n => n + 1)}
        />
      )}
    </div>
  );
}

/* ============================================================
 * Venue gallery
 * ============================================================ */

function VenueDialog({
  venue,
  onClose,
  onCreator,
  focused,
}) {
  const dialog = useRef(null);
  const close = useRef(null);

  useEffect(() => {
    const previous = document.activeElement;

    close.current?.focus();

    const key = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }

      if (event.key !== 'Tab') {
        return;
      }

      const nodes = [
        ...dialog.current.querySelectorAll(
          'button:not(:disabled),a[href],video[controls]'
        ),
      ].filter(node =>
        node.getClientRects().length
      );

      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last?.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', key);

    return () => {
      document.removeEventListener(
        'keydown',
        key
      );

      if (previous?.isConnected) {
        previous.focus();
      }
    };
  }, [onClose]);

  const directions =
    'https://www.google.com/maps/dir/?api=1&destination=' +
    encodeURIComponent(
      `${venue.lat},${venue.lng}`
    );

  return (
    <div
      className="cmap-shade"
      onMouseDown={event => {
        if (
          event.target === event.currentTarget
        ) {
          onClose();
        }
      }}
    >
      <section
        ref={dialog}
        className="cmap-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cmap-venue-title"
      >
        <header className="cmap-dialog-head">
          <p className="cmap-eyebrow">
            THROUGH THE CREATOR'S EYES
          </p>

          <h2 id="cmap-venue-title">
            {venue.name}
          </h2>

          <p className="cmap-address">
            {[
              venue.address,
              venue.area,
              venue.city,
            ].filter(Boolean).join(' · ')}
          </p>

          <button
            ref={close}
            type="button"
            className="cmap-icon-button cmap-dialog-close"
            aria-label="Close venue"
            onClick={onClose}
          >
            <Icon name="close" />
          </button>

          <div className="cmap-dialog-actions">
            <External
              href={venue.booking_url}
              className="cmap-button cmap-dark"
            >
              Book / visit website
              <Icon name="external" size={16} />
            </External>

            <External
              href={directions}
              className="cmap-button"
            >
              <Icon name="near" size={16} />
              Directions
            </External>

            <span>
              {plural(
                venue.media.length,
                'post'
              )}
            </span>
          </div>
        </header>

        <div className="cmap-gallery">
          {venue.media.map(item => (
            <article
              key={`${item.creator}-${item.url}`}
            >
              <div className="cmap-byline">
                <span className="cmap-avatar">
                  {initials(item.creator)}
                </span>

                <button
                  type="button"
                  onClick={() =>
                    onCreator(item.creator)
                  }
                >
                  {item.creator}
                </button>
              </div>

              <Media
                item={item}
                label={
                  `${venue.name}, by ${item.creator}`
                }
                enabled={focused}
                controls
              />

              <External
                href={
                  item.original_url || item.url
                }
                className="cmap-original"
              >
                Open original
                <Icon
                  name="external"
                  size={13}
                />
              </External>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ============================================================
 * Main public web screen
 * ============================================================ */

function CreatorMapWeb({
  route,
  navigation,
}) {
  const focused = useIsFocused();

  const creator =
    typeof route?.params?.Promoter === 'string'
      ? route.params.Promoter.trim()
      : '';

  const [mode, setMode] = useState('map');

  const [data, setData] = useState({
    key: creator,
    creators: [],
    venues: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);

  const [query, setQuery] = useState('');
  const [location, setLocation] = useState(null);
  const [radius, setRadius] = useState(10);

  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] =
    useState('');

  const [selected, setSelected] = useState(null);
  const [peek, setPeek] = useState(null);
  const [hovered, setHovered] = useState(null);

  const [limit, setLimit] = useState(BATCH);
  const [notice, setNotice] = useState('');

  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState('');
  const [mapRetry, setMapRetry] = useState(0);

  const mapNode = useRef(null);
  const mapState = useRef(null);
  const feedNode = useRef(null);
  const sentinel = useRef(null);

  const enterTimer = useRef(null);
  const leaveTimer = useRef(null);
  const toastTimer = useRef(null);
  const geoRequest = useRef(0);

  const alive = useRef(true);
  const latest = useRef({});
  const pendingFit = useRef(true);
  const target = useRef(null);
  const reduced = useRef(false);

  const closeDialog = useCallback(() => {
    setSelected(null);
  }, []);

  const dismissPeek = useCallback(() => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
    setPeek(null);
  }, []);

  const chooseCreator = useCallback(name => {
    setSelected(null);
    dismissPeek();
    setQuery('');

    navigation.navigate('CreatorMap', {
      Promoter: name || undefined,
    });
  }, [navigation, dismissPeek]);

  const toast = useCallback(text => {
    clearTimeout(toastTimer.current);
    setNotice(text);

    toastTimer.current = setTimeout(
      () => setNotice(''),
      3200
    );
  }, []);

  useEffect(() => {
    alive.current = true;

    reduced.current = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    return () => {
      alive.current = false;
      geoRequest.current++;

      clearTimeout(enterTimer.current);
      clearTimeout(leaveTimer.current);
      clearTimeout(toastTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!focused) {
      dismissPeek();
      setHovered(null);
      return undefined;
    }

    const old = document.title;

    document.title = creator
      ? `${creator}'s map | collabbay`
      : 'The creator map | collabbay';

    return () => {
      document.title = old;
    };
  }, [
    creator,
    focused,
    dismissPeek,
  ]);

  /* ---------------- Fetch PHP data ---------------- */

  useEffect(() => {
    const controller = new AbortController();

    let current = true;
    let timedOut = false;

    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 25000);

    setLoading(true);
    setError('');
    setSelected(null);
    dismissPeek();

    setData(old => ({
      key: creator,
      creators: old.creators,
      venues: [],
    }));

    (async () => {
      try {
        const url = new URL(API);

        if (creator) {
          url.searchParams.set(
            'creator',
            creator
          );
        }

        const response = await fetch(
          url.href,
          {
            signal: controller.signal,
            credentials: 'omit',
            headers: {
              Accept: 'application/json',
            },
          }
        );

        const text = await response.text();

        let payload;

        try {
          payload = JSON.parse(text);
        } catch (_) {
          throw new Error(
            'The API returned invalid JSON. Check the PHP URL.'
          );
        }

        if (
          !response.ok ||
          !payload?.ok
        ) {
          throw new Error(
            `${payload?.message || 'Could not load the map.'}` +
            `${payload?.reference ? ` Reference: ${payload.reference}` : ''}`
          );
        }

        const clean = normalise(payload);

        if (current) {
          setData({
            ...clean,
            key: creator,
          });
        }
      } catch (err) {
        if (current) {
          setError(
            timedOut
              ? 'The request timed out. Please retry.'
              : err.message ||
                'Could not load the map.'
          );
        }
      } finally {
        clearTimeout(timer);

        if (current) {
          setLoading(false);
        }
      }
    })();

    return () => {
      current = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    creator,
    reload,
    dismissPeek,
  ]);

  /* ---------------- Shared map/feed filters ---------------- */

  const venues = useMemo(() => {
    if (data.key !== creator) {
      return [];
    }

    const term = query
      .trim()
      .toLocaleLowerCase();

    return data.venues
      .map(venue => ({
        ...venue,
        distance: location
          ? distanceKm(location, venue)
          : null,
      }))
      .filter(venue =>
        (
          !term ||
          [
            venue.name,
            venue.area,
            venue.city,
            venue.address,
            ...venue.media.map(
              media => media.creator
            ),
          ]
            .join(' ')
            .toLocaleLowerCase()
            .includes(term)
        ) &&
        (
          !location ||
          radius === 0 ||
          venue.distance <= radius
        )
      )
      .sort((a, b) =>
        location
          ? a.distance - b.distance
          : (
              dateScore(b.latest_at) -
              dateScore(a.latest_at)
            ) ||
            a.name.localeCompare(b.name)
      );
  }, [
    data,
    creator,
    query,
    location,
    radius,
  ]);

  const postCount = useMemo(() =>
    venues.reduce(
      (total, venue) =>
        total + venue.media.length,
      0
    ),
  [venues]);

  latest.current = {
    venues,
    location,
    mode,
    focused,
    loading,
  };

  useEffect(() => {
    setLimit(BATCH);
    setSelected(null);
    setHovered(null);
    dismissPeek();

    if (feedNode.current) {
      feedNode.current.scrollTop = 0;
    }
  }, [venues, dismissPeek]);

  /* ---------------- Map positioning ---------------- */

  const fitMap = useCallback(() => {
    const state = mapState.current;

    if (
      !state ||
      latest.current.mode !== 'map'
    ) {
      pendingFit.current = true;
      return;
    }

    const points = latest.current.venues.map(
      venue => [venue.lat, venue.lng]
    );

    if (latest.current.location) {
      points.push([
        latest.current.location.lat,
        latest.current.location.lng,
      ]);
    }

    if (!points.length) {
      pendingFit.current = false;
      return;
    }

    const mobile = window.matchMedia(
      '(max-width: 760px)'
    ).matches;

    if (points.length === 1) {
      state.map.setView(
        points[0],
        15,
        {
          animate: !reduced.current,
        }
      );
    } else {
      state.map.fitBounds(points, {
        paddingTopLeft: [
          45,
          mobile ? 205 : 150,
        ],
        paddingBottomRight: [45, 115],
        maxZoom: 15,
        animate: !reduced.current,
      });
    }

    pendingFit.current = false;
  }, []);

  const showPeek = useCallback(
    (venue, marker) => {
      const state = mapState.current;

      if (
        !state ||
        !latest.current.focused ||
        latest.current.mode !== 'map'
      ) {
        return;
      }

      const point =
        state.map.latLngToContainerPoint(
          marker.getLatLng()
        );

      const size = state.map.getSize();

      setPeek({
        venue,

        x: Math.max(
          12,
          Math.min(
            size.x - 248,
            point.x - 120
          )
        ),

        y: Math.max(
          115,
          Math.min(
            size.y - 360,
            point.y > 430
              ? point.y - 305
              : point.y + 18
          )
        ),
      });
    },
    []
  );

  const leavePeek = useCallback(() => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);

    leaveTimer.current = setTimeout(
      () => setPeek(null),
      230
    );
  }, []);

  /* ---------------- Create Leaflet map ---------------- */

  useEffect(() => {
    let cancelled = false;
    let observer;
    let frame;
    let localState;

    setMapReady(false);
    setMapError('');

    loadMapLibrary()
      .then(L => {
        if (
          cancelled ||
          !mapNode.current
        ) {
          return;
        }

        const map = L.map(
          mapNode.current,
          {
            zoomControl: false,
            preferCanvas: true,
          }
        ).setView(
          [51.5074, -0.1278],
          12
        );

        const tiles = L.tileLayer(
          TILE_URL,
          {
            maxZoom: 19,
            attribution: TILE_CREDIT,
          }
        ).addTo(map);

        L.control.zoom({
          position: 'bottomright',
        }).addTo(map);

        const group =
          L.markerClusterGroup
            ? L.markerClusterGroup({
                showCoverageOnHover: false,
                maxClusterRadius: 46,
                animate: !reduced.current,

                iconCreateFunction: cluster =>
                  L.divIcon({
                    className: 'cmap-cluster',
                    html: String(
                      cluster.getChildCount()
                    ),
                    iconSize: [48, 48],
                  }),
              })
            : L.featureGroup();

        group.addTo(map);

        localState = {
          L,
          map,
          group,
          user: L.layerGroup().addTo(map),
          markers: new Map(),
        };

        mapState.current = localState;

        map.on(
          'movestart',
          dismissPeek
        );

        map.on(
          'click',
          dismissPeek
        );

        let tileErrors = 0;

        tiles.on('tileerror', () => {
          if (
            ++tileErrors === 6 &&
            !cancelled
          ) {
            setMapError(
              'Some map tiles did not load. Feed is still available.'
            );
          }
        });

        tiles.on('tileload', () => {
          if (
            tileErrors >= 6 &&
            !cancelled
          ) {
            setMapError('');
          }

          tileErrors = 0;
        });

        const resize = () => {
          cancelAnimationFrame(frame);

          frame = requestAnimationFrame(() => {
            if (!cancelled) {
              map.invalidateSize();
            }
          });
        };

        if (window.ResizeObserver) {
          observer = new ResizeObserver(
            resize
          );

          observer.observe(
            mapNode.current
          );
        }

        window.addEventListener(
          'resize',
          resize
        );

        localState.stopResize = () => {
          window.removeEventListener(
            'resize',
            resize
          );
        };

        setMapReady(true);
      })
      .catch(err => {
        if (!cancelled) {
          setMapError(err.message);
        }
      });

    return () => {
      cancelled = true;

      cancelAnimationFrame(frame);
      observer?.disconnect();

      if (localState) {
        localState.stopResize?.();
        localState.map.remove();

        if (
          mapState.current === localState
        ) {
          mapState.current = null;
        }
      }
    };
  }, [
    mapRetry,
    dismissPeek,
  ]);

  /* ---------------- Build pins ---------------- */

  useEffect(() => {
    const state = mapState.current;

    if (!state || !mapReady) {
      return;
    }

    const {
      L,
      group,
      markers,
    } = state;

    group.clearLayers();
    markers.clear();

    venues.forEach((venue, index) => {
      const face =
        document.createElement('span');

      face.className = 'cmap-pin-face';

      const fallback = () => {
        face.textContent = String(index + 1);
      };

      const poster = posterOf(venue);

      if (poster) {
        const image =
          document.createElement('img');

        image.src = poster;
        image.alt = '';
        image.loading = 'lazy';
        image.onerror = fallback;

        face.appendChild(image);
      } else {
        fallback();
      }

      const marker = L.marker(
        [venue.lat, venue.lng],
        {
          title: venue.name,
          alt: venue.name,
          riseOnHover: true,

          icon: L.divIcon({
            className: 'cmap-pin',
            html: face,
            iconSize: [46, 50],
            iconAnchor: [23, 48],
          }),
        }
      );

      marker.on('mouseover', () => {
        if (
          !window.matchMedia(
            '(hover: hover)'
          ).matches
        ) {
          return;
        }

        clearTimeout(leaveTimer.current);
        clearTimeout(enterTimer.current);

        enterTimer.current = setTimeout(
          () => showPeek(venue, marker),
          140
        );
      });

      marker.on(
        'mouseout',
        leavePeek
      );

      marker.on('click', () => {
        dismissPeek();
        setSelected(venue);
      });

      group.addLayer(marker);
      markers.set(venue.id, marker);
    });

    pendingFit.current = true;

    if (!loading) {
      fitMap();
    }
  }, [
    mapReady,
    venues,
    loading,
    fitMap,
    showPeek,
    dismissPeek,
    leavePeek,
  ]);

  /* ---------------- Visitor location marker ---------------- */

  useEffect(() => {
    const state = mapState.current;

    if (!state || !mapReady) {
      return;
    }

    state.user.clearLayers();

    if (!location) {
      return;
    }

    const { L, user } = state;

    L.circle(
      [location.lat, location.lng],
      {
        radius: Math.min(
          Math.max(
            location.accuracy || 0,
            20
          ),
          10000
        ),
        weight: 1,
        color: '#448bf2',
        fillOpacity: 0.08,
        interactive: false,
      }
    ).addTo(user);

    L.circleMarker(
      [location.lat, location.lng],
      {
        radius: 8,
        color: '#fff',
        weight: 3,
        fillColor: '#448bf2',
        fillOpacity: 1,
      }
    )
      .addTo(user)
      .bindTooltip(
        'Your approximate location'
      );
  }, [mapReady, location]);

  /* ---------------- Preserve map when switching views ---------------- */

  useEffect(() => {
    dismissPeek();
    setHovered(null);

    const frame = requestAnimationFrame(() => {
      const state = mapState.current;

      if (
        !state ||
        mode !== 'map' ||
        !focused
      ) {
        return;
      }

      state.map.invalidateSize();

      const venue = target.current;
      target.current = null;

      const marker =
        venue && state.markers.get(venue.id);

      if (marker) {
        pendingFit.current = false;

        state.map.setView(
          marker.getLatLng(),
          16,
          { animate: false }
        );

        if (
          state.group.zoomToShowLayer
        ) {
          state.group.zoomToShowLayer(
            marker,
            () => {
              if (alive.current) {
                marker
                  .getElement()
                  ?.classList.add(
                    'cmap-pin-selected'
                  );
              }
            }
          );
        } else {
          marker
            .getElement()
            ?.classList.add(
              'cmap-pin-selected'
            );
        }
      } else if (pendingFit.current) {
        fitMap();
      }
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [
    mode,
    focused,
    mapReady,
    fitMap,
    dismissPeek,
  ]);

  /* ---------------- Feed pagination ---------------- */

  useEffect(() => {
    if (
      !sentinel.current ||
      mode !== 'feed' ||
      !window.IntersectionObserver ||
      limit >= venues.length
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLimit(n =>
            Math.min(
              n + BATCH,
              venues.length
            )
          );
        }
      },
      {
        root: feedNode.current,
        rootMargin: '400px',
      }
    );

    observer.observe(sentinel.current);

    return () => observer.disconnect();
  }, [
    mode,
    limit,
    venues.length,
  ]);

  /* ---------------- User actions ---------------- */

  const nearMe = () => {
    setLocationError('');

    if (location || locating) {
      geoRequest.current++;

      setLocation(null);
      setLocating(false);

      return;
    }

    if (
      !window.isSecureContext ||
      !navigator.geolocation
    ) {
      setLocationError(
        'Location needs HTTPS and a browser with location support.'
      );

      return;
    }

    const request = ++geoRequest.current;

    setLocating(true);

    navigator.geolocation.getCurrentPosition(
      position => {
        if (
          !alive.current ||
          request !== geoRequest.current
        ) {
          return;
        }

        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });

        setLocating(false);
      },

      err => {
        if (
          !alive.current ||
          request !== geoRequest.current
        ) {
          return;
        }

        setLocating(false);

        setLocationError(
          err.code === 1
            ? 'Location permission was denied. Allow location for this site, or search an area instead.'
            : 'We could not find your location. Try again or search an area.'
        );
      },

      {
        enableHighAccuracy: false,
        timeout: 12000,
        maximumAge: 60000,
      }
    );
  };

  const clearFilters = () => {
    setQuery('');
    setLocation(null);
    setLocationError('');

    geoRequest.current++;

    setLocating(false);
  };

  const share = async () => {
    const url =
      ORIGIN + creatorPath(creator);

    try {
      if (
        navigator.share &&
        window.matchMedia(
          '(pointer: coarse)'
        ).matches
      ) {
        await navigator.share({
          title: document.title,
          url,
        });
      } else if (
        navigator.clipboard?.writeText
      ) {
        await navigator.clipboard.writeText(
          url
        );

        toast('Map link copied.');
      } else {
        window.prompt(
          'Copy this map link:',
          url
        );
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        window.prompt(
          'Copy this map link:',
          url
        );
      }
    }
  };

  const showOnMap = venue => {
    target.current = venue;
    setSelected(null);
    setMode('map');
  };

  const creators =
    creator &&
    !data.creators.includes(creator)
      ? [creator, ...data.creators]
      : data.creators;

  const filtered = Boolean(
    query || location
  );

  const showEmpty =
    !loading &&
    !error &&
    !venues.length;

  /* ---------------- Interface ---------------- */

  return (
    <div
      className={
        `cmap-root cmap-mode-${mode}`
      }
    >
      <style>{CSS}</style>

      {/* Main map */}

      <section
        className="cmap-map-panel"
        hidden={mode !== 'map'}
        aria-label="Creator venue map"
      >
        <div
          className="cmap-map"
          ref={mapNode}
        />

        <div className="cmap-map-wash" />

        <div className="cmap-summary cmap-glass">
          <p className="cmap-eyebrow">
            {creator
              ? 'THE CREATOR’S GUIDE'
              : 'A NEW WAY TO EXPLORE'}
          </p>

          <h1>
            {creator ? (
              <>
                {creator}
                <span>’s map.</span>
              </>
            ) : (
              <>
                Good places.
                <br />
                <span>
                  Through their eyes.
                </span>
              </>
            )}
          </h1>

          <p className="cmap-summary-count">
            <span className="cmap-dot" />

            {loading
              ? 'Finding the good places…'
              : (
                  plural(
                    venues.length,
                    'place'
                  ) +
                  (
                    location
                      ? ' near you'
                      : ' to explore'
                  )
                )}
          </p>

          {filtered && (
            <button
              type="button"
              className="cmap-reset"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          )}
        </div>

        <button
          type="button"
          className="cmap-icon-button cmap-fit cmap-glass"
          aria-label="Fit all places"
          onClick={fitMap}
          disabled={!mapReady}
        >
          <Icon name="fit" />
        </button>

        {peek && focused && !selected && (
          <article
            className="cmap-peek"
            style={{
              left: peek.x,
              top: peek.y,
            }}
            onMouseEnter={() => {
              clearTimeout(
                leaveTimer.current
              );
            }}
            onMouseLeave={leavePeek}
          >
            <Media
              key={peek.venue.id}
              item={previewOf(peek.venue)}
              label={
                `${peek.venue.name} preview`
              }
              active
              enabled={
                focused &&
                mode === 'map'
              }
              poster={posterOf(peek.venue)}
            />

            <button
              type="button"
              className="cmap-peek-info"
              onClick={() => {
                setSelected(peek.venue);
                dismissPeek();
              }}
            >
              <span>
                <strong>
                  {peek.venue.name}
                </strong>

                <small>
                  {
                    previewOf(
                      peek.venue
                    ).creator
                  }
                </small>
              </span>

              <Icon
                name="arrow"
                size={18}
              />
            </button>
          </article>
        )}

        <div className="cmap-map-hint">
          <span className="cmap-desktop">
            Hover to peek. Click to explore.
          </span>

          <span className="cmap-touch">
            Tap a pin to explore.
          </span>
        </div>

        {!mapReady && !mapError && (
          <div className="cmap-map-loading">
            <span className="cmap-spinner" />
            Loading the map…
          </div>
        )}

        {mapError && (
          <div
            className="cmap-map-error cmap-glass"
            role="status"
          >
            {mapError}

            <button
              type="button"
              onClick={() =>
                setMapRetry(n => n + 1)
              }
            >
              Retry map
            </button>
          </div>
        )}
      </section>

      {/* Scrollable feed */}

      <section
        ref={feedNode}
        className="cmap-feed"
        hidden={mode !== 'feed'}
        aria-label="Scrollable creator feed"
        aria-busy={loading}
      >
        <div className="cmap-feed-inner">
          <div className="cmap-feed-title">
            <div>
              <p className="cmap-eyebrow">
                THE DISCOVERY FEED
              </p>

              <h1>
                {creator
                  ? `${creator}’s places.`
                  : location
                    ? 'A little closer to you.'
                    : 'Your next good find.'}
              </h1>

              <p>
                Real creator visits.
                Watch, explore, make a plan.
              </p>
            </div>

            <span>
              {loading
                ? 'Loading…'
                : (
                    `${plural(venues.length, 'place')}` +
                    ` · ${plural(postCount, 'post')}`
                  )}
            </span>
          </div>

          {filtered && (
            <button
              type="button"
              className="cmap-reset cmap-feed-reset"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          )}

          {loading ? (
            <div className="cmap-feed-grid">
              {[0, 1, 2].map(index => (
                <div
                  className="cmap-skeleton"
                  key={index}
                >
                  <div />
                  <span />
                  <span />
                </div>
              ))}
            </div>
          ) : (
            <div className="cmap-feed-grid">
              {venues
                .slice(0, limit)
                .map(venue => {
                  const item =
                    previewOf(venue);

                  const authors = [
                    ...new Set(
                      venue.media.map(
                        media =>
                          media.creator
                      )
                    ),
                  ];

                  return (
                    <article
                      key={venue.id}
                      className="cmap-card"
                      onMouseEnter={() =>
                        setHovered(venue.id)
                      }
                      onMouseLeave={() =>
                        setHovered(null)
                      }
                    >
                      <div className="cmap-card-visual">
                        <Media
                          item={item}
                          label={
                            `${venue.name}, by ${item.creator}`
                          }
                          controls
                          active={
                            hovered ===
                              venue.id &&
                            !selected
                          }
                          enabled={
                            focused &&
                            mode === 'feed' &&
                            !selected
                          }
                          poster={
                            posterOf(venue)
                          }
                        />

                        <button
                          type="button"
                          className="cmap-count-badge"
                          onClick={() =>
                            setSelected(venue)
                          }
                        >
                          {plural(
                            venue.media.length,
                            'post'
                          )}

                          <Icon
                            name="arrow"
                            size={12}
                          />
                        </button>
                      </div>

                      <div className="cmap-card-body">
                        <div className="cmap-card-meta">
                          <span>
                            {[
                              venue.area,
                              venue.city,
                            ]
                              .filter(Boolean)
                              .join(' · ') ||
                              venue.address ||
                              'On the map'}
                          </span>

                          {location && (
                            <span>
                              {distanceLabel(
                                venue.distance
                              )}
                            </span>
                          )}
                        </div>

                        <h2>
                          <button
                            type="button"
                            onClick={() =>
                              setSelected(
                                venue
                              )
                            }
                          >
                            {venue.name}
                          </button>
                        </h2>

                        <div className="cmap-byline">
                          <span className="cmap-avatar">
                            {initials(
                              item.creator
                            )}
                          </span>

                          <button
                            type="button"
                            onClick={() =>
                              chooseCreator(
                                item.creator
                              )
                            }
                          >
                            {item.creator}
                          </button>

                          {authors.length > 1 && (
                            <small>
                              +{
                                authors.length -
                                1
                              } more
                            </small>
                          )}
                        </div>

                        <div className="cmap-card-actions">
                          <button
                            type="button"
                            className="cmap-explore"
                            onClick={() =>
                              setSelected(
                                venue
                              )
                            }
                          >
                            Explore

                            <Icon
                              name="arrow"
                              size={15}
                            />
                          </button>

                          <button
                            type="button"
                            className="cmap-pin-button"
                            aria-label={
                              `Show ${venue.name} on map`
                            }
                            onClick={() =>
                              showOnMap(venue)
                            }
                          >
                            <Icon
                              name="pin"
                              size={17}
                            />
                          </button>

                          <External
                            href={
                              venue.booking_url
                            }
                            className="cmap-visit"
                          >
                            Visit website

                            <Icon
                              name="external"
                              size={14}
                            />
                          </External>
                        </div>
                      </div>
                    </article>
                  );
                })}
            </div>
          )}

          {
            mode === 'feed' &&
            !loading &&
            limit < venues.length && (
              <div
                ref={sentinel}
                className="cmap-more"
              >
                <button
                  type="button"
                  className="cmap-button"
                  onClick={() =>
                    setLimit(n => n + BATCH)
                  }
                >
                  More good places
                </button>
              </div>
            )
          }

          {
            !loading &&
            venues.length > 0 && (
              <p className="cmap-feed-end">
                {plural(
                  Math.min(
                    limit,
                    venues.length
                  ),
                  'place'
                )}

                {' shown. A little inspiration, courtesy of the creators.'}
              </p>
            )
          }
        </div>
      </section>

      {/* Floating top controls */}

      <header className="cmap-header">
        <a
          className="cmap-brand cmap-glass"
          href={ORIGIN}
          aria-label="collabbay home"
        >
          collabbay<sup>®</sup>
          <span>THE CREATOR MAP</span>
        </a>

        <div className="cmap-controls cmap-glass">
          <label className="cmap-search">
            <Icon
              name="search"
              size={18}
            />

            <input
              type="search"
              placeholder="Find somewhere good…"
              aria-label="Search places, areas or creators"
              value={query}
              onChange={event =>
                setQuery(
                  event.target.value
                )
              }
            />
          </label>

          <div className="cmap-filters">
            <select
              aria-label="Choose a creator"
              value={creator}
              onChange={event =>
                chooseCreator(
                  event.target.value
                )
              }
            >
              <option value="">
                All creators
              </option>

              {creators.map(name => (
                <option
                  key={name}
                  value={name}
                >
                  {name}
                </option>
              ))}
            </select>

            <button
              type="button"
              className={
                `cmap-near ${
                  location ? 'is-on' : ''
                }`
              }
              onClick={nearMe}
              aria-pressed={
                Boolean(location)
              }
            >
              <Icon
                name="near"
                size={17}
              />

              <span>
                {locating
                  ? 'Cancel'
                  : 'Near me'}
              </span>

              {locating && (
                <span className="cmap-spinner" />
              )}
            </button>

            {location && (
              <select
                className="cmap-radius"
                aria-label="Search radius"
                value={radius}
                onChange={event =>
                  setRadius(
                    Number(
                      event.target.value
                    )
                  )
                }
              >
                {[2, 5, 10, 25, 50, 0]
                  .map(km => (
                    <option
                      key={km}
                      value={km}
                    >
                      {km
                        ? `${km} km`
                        : 'Any distance'}
                    </option>
                  ))}
              </select>
            )}
          </div>
        </div>

        <button
          type="button"
          className="cmap-share cmap-icon-button cmap-glass"
          aria-label="Share this creator map"
          onClick={share}
        >
          <Icon name="share" />
        </button>
      </header>

      {/* Empty and error states */}

      {showEmpty && (
        <div className="cmap-empty cmap-glass">
          <Icon
            name={
              location ? 'near' : 'pin'
            }
            size={30}
          />

          <h2>
            {location
              ? 'Nothing in this radius yet.'
              : 'No places found.'}
          </h2>

          <p>
            {location
              ? 'Try a wider radius or turn off Near me.'
              : filtered
                ? 'Try another name or clear your search.'
                : 'No mapped public content is available for this selection yet.'}
          </p>

          {filtered && (
            <button
              type="button"
              className="cmap-button"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          )}

          {creator && (
            <button
              type="button"
              className="cmap-button"
              onClick={() =>
                chooseCreator('')
              }
            >
              Show all creators
            </button>
          )}
        </div>
      )}

      {(error || locationError) && (
        <div
          className="cmap-alert cmap-glass"
          role="alert"
        >
          <span>
            {error || locationError}
          </span>

          {error ? (
            <button
              type="button"
              onClick={() =>
                setReload(n => n + 1)
              }
            >
              Retry
            </button>
          ) : (
            <button
              type="button"
              aria-label="Dismiss message"
              onClick={() =>
                setLocationError('')
              }
            >
              <Icon
                name="close"
                size={16}
              />
            </button>
          )}
        </div>
      )}

      {/* Only two bottom navigation buttons */}

      <nav
        className="cmap-dock"
        aria-label="Map or feed view"
      >
        <span
          className={
            `cmap-dock-active ${
              mode === 'feed'
                ? 'is-feed'
                : ''
            }`
          }
          aria-hidden="true"
        />

        {[
          ['map', 'map', 'Map'],
          ['feed', 'feed', 'Feed'],
        ].map(([value, icon, label]) => (
          <button
            key={value}
            type="button"
            aria-pressed={mode === value}
            onClick={() =>
              setMode(value)
            }
          >
            <Icon
              name={icon}
              size={19}
            />

            <span>{label}</span>
          </button>
        ))}
      </nav>

      {notice && (
        <div
          className="cmap-toast"
          role="status"
        >
          {notice}
        </div>
      )}

      {selected && focused && (
        <VenueDialog
          venue={selected}
          onClose={closeDialog}
          onCreator={chooseCreator}
          focused={focused}
        />
      )}
    </div>
  );
}

/* ============================================================
 * Export with native fallback
 * ============================================================ */

export default function CreatorMap(props) {
  if (Platform.OS !== 'web') {
    const creator =
      typeof props.route?.params?.Promoter === 'string'
        ? props.route.params.Promoter
        : '';

    return (
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f6f0',
          padding: 28,
        }}
      >
        <Text
          style={{
            fontSize: 25,
            marginBottom: 14,
          }}
        >
          The creator map
        </Text>

        <Text
          style={{
            textAlign: 'center',
            marginBottom: 20,
          }}
        >
          Open the map and feed in your browser.
        </Text>

        <Pressable
          style={{
            backgroundColor: '#dff58b',
            padding: 16,
            borderRadius: 14,
          }}
          onPress={() =>
            Linking.openURL(
              ORIGIN +
              creatorPath(creator)
            )
          }
        >
          <Text>Explore the map</Text>
        </Pressable>
      </View>
    );
  }

  return <CreatorMapWeb {...props} />;
}

/* ============================================================
 * All styling stays inside this file
 * ============================================================ */

const CSS = `
.cmap-root{
  --ink:#1c2924;
  --muted:#7a837b;
  --line:#dce2d6;
  --lime:#dff58b;
  --head:112px;
  position:relative;
  isolation:isolate;
  width:100%;
  height:100vh;
  height:100dvh;
  overflow:hidden;
  background:#f4f5ef;
  color:var(--ink);
  font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
}
.cmap-root *{box-sizing:border-box}
.cmap-root [hidden]{display:none!important}
.cmap-root button,.cmap-root input,.cmap-root select{font:inherit;color:inherit}
.cmap-root button{cursor:pointer;border:0}
.cmap-root button:disabled{opacity:.45;cursor:default}
.cmap-root a{color:inherit;text-decoration:none}
.cmap-root button,.cmap-root a{-webkit-tap-highlight-color:transparent;touch-action:manipulation}
.cmap-root button:focus-visible,.cmap-root a:focus-visible,.cmap-root input:focus-visible,.cmap-root select:focus-visible{outline:3px solid #779c42;outline-offset:3px}
.cmap-root h1,.cmap-root h2,.cmap-root p{margin:0}
.cmap-root svg{flex:none}

.cmap-glass{
  background:rgba(255,255,255,.82);
  border:1px solid rgba(255,255,255,.95);
  box-shadow:0 8px 32px #25362110,0 1px 3px #263b2110;
  backdrop-filter:blur(22px) saturate(140%);
  -webkit-backdrop-filter:blur(22px) saturate(140%);
}

.cmap-header{
  position:absolute;
  z-index:700;
  top:22px;
  left:26px;
  right:26px;
  display:grid;
  grid-template-columns:175px minmax(0,800px) 48px;
  justify-content:space-between;
  align-items:center;
  gap:18px;
  pointer-events:none;
}
.cmap-header>*{pointer-events:auto}
.cmap-brand{font-size:28px;letter-spacing:-1.5px;padding:12px 18px 11px;border-radius:20px;line-height:1.05;width:fit-content;font-weight:400}
.cmap-brand sup{font-size:10px;letter-spacing:0;vertical-align:top;line-height:20px}
.cmap-brand>span{display:block;font-size:7px;letter-spacing:2px;font-weight:650;margin-top:6px;color:#7b8777}
.cmap-controls{display:flex;align-items:center;min-width:0;border-radius:20px;padding:7px 8px 7px 17px;min-height:60px}
.cmap-search{display:flex;align-items:center;gap:10px;flex:1;min-width:90px;color:#7b8777}
.cmap-search input{width:100%;min-width:0;background:none;border:0;padding:10px 5px 10px 0;outline:none;font-size:13px}
.cmap-search input::placeholder{color:#929a8d}
.cmap-filters{display:flex;align-items:center;gap:5px;min-width:0}
.cmap-filters select{background:transparent;border:0;border-left:1px solid #dfe5d7;max-width:185px;padding:10px 8px;font-size:12px;outline:none;min-width:0}
.cmap-filters .cmap-radius{max-width:107px;font-size:11px;padding-left:5px}
.cmap-near{display:flex;align-items:center;justify-content:center;gap:7px;padding:11px 12px;border-radius:13px;background:#eef1e8;white-space:nowrap;font-size:12px!important}
.cmap-near.is-on{background:var(--lime)}
.cmap-icon-button{width:45px;height:45px;border-radius:15px;display:grid;place-items:center;background:white;border:1px solid var(--line)!important;flex:none}
.cmap-share{width:48px;height:48px}
.cmap-eyebrow{font-size:8px;letter-spacing:1.8px;font-weight:700;color:#839176;margin-bottom:8px!important}
.cmap-button{display:inline-flex;align-items:center;justify-content:center;gap:8px;padding:10px 14px;border-radius:12px;background:white;border:1px solid var(--line)!important;font-size:12px!important;font-weight:550;min-height:41px}
.cmap-dark{background:var(--ink)!important;color:white!important;border-color:var(--ink)!important}

.cmap-map-panel,.cmap-map{position:absolute;inset:0}
.cmap-map{z-index:0;background:#e7ede2}
.cmap-map-wash{position:absolute;inset:0;pointer-events:none;background:linear-gradient(180deg,#f4f5ef70 0%,transparent 22%,transparent 86%,#eef1e333 100%);z-index:400}
.cmap-summary{position:absolute;top:var(--head);left:26px;border-radius:22px;padding:20px 22px;max-width:300px;z-index:500}
.cmap-summary h1{font-size:29px;letter-spacing:-1.1px;line-height:1.12;font-weight:620;overflow-wrap:anywhere}
.cmap-summary h1 span{color:#849079;font-weight:400}
.cmap-summary-count{margin-top:14px!important;display:flex;gap:8px;align-items:center;font-size:11px;color:#7c8774}
.cmap-dot{height:6px;width:6px;background:#819b51;border-radius:50%;flex:none}
.cmap-reset{background:none;color:#65754f!important;text-decoration:underline;text-underline-offset:4px;padding:0;margin-top:9px;font-size:11px!important}
.cmap-fit{position:absolute;top:var(--head);right:26px;z-index:500}
.cmap-map-hint{position:absolute;bottom:28px;left:26px;color:#657359;font-size:10px;padding:7px 11px;background:#ffffffe3;border:1px solid white;border-radius:20px;z-index:450;pointer-events:none}
.cmap-touch{display:none}
.cmap-map-loading{position:absolute;left:50%;top:49%;transform:translate(-50%,-50%);background:#fffffff0;border:1px solid white;box-shadow:0 5px 30px #1c302311;padding:15px 22px;border-radius:18px;display:flex;align-items:center;gap:10px;z-index:500;font-size:12px}
.cmap-spinner{display:inline-block;width:15px;height:15px;border:2px solid #b4c5a2;border-top-color:#536e3a;border-radius:50%;animation:cmap-spin .8s linear infinite;flex:none}
.cmap-map-error{position:absolute;bottom:110px;left:50%;transform:translateX(-50%);z-index:600;border-radius:15px;padding:13px 17px;max-width:calc(100% - 28px);font-size:12px}
.cmap-map-error button{background:none;text-decoration:underline;margin-left:10px}
.cmap-root .leaflet-tile-pane{filter:grayscale(.82) saturate(.45) brightness(1.035)}
.cmap-root .leaflet-container{font:inherit}
.cmap-root .leaflet-bar{border:0!important;border-radius:14px;overflow:hidden;box-shadow:0 5px 25px #283d251a}
.cmap-root .leaflet-bar a{width:38px;height:38px;line-height:38px;border-bottom-color:#e8ece3;color:var(--ink);background:#fffffff0}
.cmap-root .leaflet-control-zoom{margin-right:28px;margin-bottom:24px}
.cmap-root .leaflet-control-attribution{background:#ffffffe8;font-size:9px;padding:3px 7px;border-radius:8px 0 0 0}
.cmap-root .leaflet-control-attribution a{color:#657354}

.cmap-pin{background:none;border:0}
.cmap-pin-face{display:flex;align-items:center;justify-content:center;width:44px;height:44px;border:3px solid white;background:var(--ink);color:var(--lime);font-size:13px;font-weight:650;border-radius:17px;box-shadow:0 4px 14px #14291842;position:relative;transition:transform .18s,box-shadow .18s}
.cmap-pin-face:after{content:"";position:absolute;bottom:-7px;left:15px;width:9px;height:9px;background:white;transform:rotate(45deg);border-radius:1px;z-index:-1}
.cmap-root .cmap-pin-face img{height:100%;width:100%;object-fit:cover;border-radius:13px}
.cmap-pin:hover .cmap-pin-face,.cmap-pin-selected .cmap-pin-face{transform:translateY(-3px);box-shadow:0 0 0 4px #dff58b88,0 7px 20px #14291855;border-color:var(--lime)}
.cmap-cluster{display:flex;align-items:center;justify-content:center;background:var(--ink);color:var(--lime);border:5px solid #dff58bd9;border-radius:50%;font-size:13px;font-weight:650;box-shadow:0 6px 24px #14291833}
.cmap-peek{position:absolute;z-index:650;width:236px;border:5px solid white;border-radius:21px;background:white;overflow:hidden;box-shadow:0 12px 50px #182e3140;animation:cmap-rise .16s ease}
.cmap-peek .cmap-media{height:206px;aspect-ratio:auto;border-radius:15px}
.cmap-peek-info{background:white;width:100%;display:flex;align-items:center;justify-content:space-between;padding:12px 8px 9px;text-align:left;gap:10px}
.cmap-peek-info>span{min-width:0}
.cmap-peek-info strong{display:block;font-size:13px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis}
.cmap-peek-info small{font-size:10px;display:block;margin-top:3px;color:var(--muted);overflow:hidden;white-space:nowrap;text-overflow:ellipsis}

.cmap-feed{position:absolute;inset:0;overflow:auto;overscroll-behavior:contain;background:radial-gradient(ellipse at 10% 0%,#e8eedb 0%,transparent 45%),#f4f5ef;scrollbar-width:thin;scrollbar-color:#c4ceba transparent;padding:calc(var(--head) + 24px) 30px 135px}
.cmap-feed-inner{max-width:1220px;margin:0 auto}
.cmap-feed-title{display:flex;align-items:flex-end;justify-content:space-between;gap:24px;margin:0 0 30px}
.cmap-feed-title h1{font-size:clamp(28px,3.3vw,44px);line-height:1.12;letter-spacing:-1.7px;font-weight:560;overflow-wrap:anywhere}
.cmap-feed-title p:not(.cmap-eyebrow){font-size:12px;color:var(--muted);margin-top:11px}
.cmap-feed-title>span{font-size:11px;color:#849078;white-space:nowrap;padding-bottom:3px}
.cmap-feed-reset{margin:-12px 0 23px}
.cmap-feed-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:24px;align-items:start}
.cmap-card{background:white;border:1px solid #e1e6d9;border-radius:24px;overflow:hidden;box-shadow:0 4px 18px #26321e03;transition:box-shadow .2s,transform .2s}
.cmap-card:hover{box-shadow:0 10px 35px #23361a10;transform:translateY(-2px)}
.cmap-card-visual{position:relative}
.cmap-media{position:relative;aspect-ratio:4/3;background:#e4e9da;overflow:hidden;display:flex;align-items:center;justify-content:center}
.cmap-media img,.cmap-media video{display:block;width:100%;height:100%;object-fit:cover}
.cmap-media video{background:#1b2621}
.cmap-media-placeholder,.cmap-media-empty{width:100%;height:100%;min-height:150px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;background:linear-gradient(135deg,#e8eedc,#d8e1c7);color:#738461;padding:20px}
.cmap-media-empty strong{font-size:13px;font-weight:550}
.cmap-media-empty a{display:inline-flex;gap:7px;align-items:center;text-decoration:underline;text-underline-offset:4px;font-size:11px}
.cmap-video-tag{position:absolute;top:12px;left:12px;display:flex;gap:4px;align-items:center;background:#13201788;color:white;border:1px solid #ffffff35;font-size:7px;letter-spacing:1.2px;padding:4px 6px;border-radius:6px;pointer-events:none;backdrop-filter:blur(8px)}
.cmap-play{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:46px;height:46px;border-radius:50%;display:grid;place-items:center;background:#ffffffdd;box-shadow:0 5px 20px #0002}
.cmap-count-badge{position:absolute;right:12px;top:12px;display:flex;align-items:center;gap:5px;background:#ffffffed;padding:6px 9px;border:1px solid white!important;border-radius:9px;font-size:10px!important;box-shadow:0 2px 10px #0001;backdrop-filter:blur(10px)}
.cmap-card-body{padding:17px 19px 16px}
.cmap-card-meta{display:flex;justify-content:space-between;gap:8px;color:#839074;font-size:10px}
.cmap-card-meta>span:first-child{overflow:hidden;white-space:nowrap;text-overflow:ellipsis}
.cmap-card-meta>span+span{flex:none}
.cmap-card h2{font-size:23px;letter-spacing:-.8px;line-height:1.2;font-weight:570;margin:6px 0 13px}
.cmap-card h2 button{background:none;padding:0;text-align:left;font:inherit;color:inherit}
.cmap-byline{display:flex;align-items:center;gap:7px;min-width:0;font-size:11px}
.cmap-byline button{background:none;padding:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:11px}
.cmap-byline small{color:#8a9381;font-size:9px;white-space:nowrap}
.cmap-avatar{display:inline-grid;place-items:center;flex:none;width:25px;height:25px;border-radius:50%;background:#f0edf7;color:#8571ad;font-size:8px;font-weight:650}
.cmap-card-actions{display:flex;align-items:center;gap:9px;margin-top:16px;padding-top:13px;border-top:1px solid #eef1e8}
.cmap-explore{display:inline-flex;align-items:center;gap:7px;background:none;padding:0;font-size:11px!important;font-weight:600}
.cmap-pin-button{display:grid;place-items:center;background:none;color:#899579!important;padding:3px}
.cmap-visit{display:inline-flex;align-items:center;gap:5px;margin-left:auto;border-radius:10px;background:var(--lime);padding:8px 10px;font-size:10px;white-space:nowrap}
.cmap-more{text-align:center;padding:35px 0 5px}
.cmap-feed-end{text-align:center;color:#98a08f;font-size:10px;padding:35px 10px 5px}
.cmap-skeleton{border-radius:24px;background:white;border:1px solid var(--line);padding-bottom:25px;overflow:hidden}
.cmap-skeleton>div{aspect-ratio:4/3;background:linear-gradient(100deg,#e5ebdc 20%,#f0f4e8 45%,#e5ebdc 70%);background-size:220% 100%;animation:cmap-shimmer 1.5s linear infinite}
.cmap-skeleton>span{display:block;height:12px;width:55%;margin:20px 20px 0;border-radius:5px;background:#e9eee1}
.cmap-skeleton>span+span{width:35%;height:8px;margin-top:11px}

/* Floating glass Map / Feed dock */

.cmap-dock{
  position:absolute;
  left:50%;
  bottom:max(24px,env(safe-area-inset-bottom));
  transform:translateX(-50%);
  z-index:900;
  display:grid;
  grid-template-columns:repeat(2,112px);
  padding:6px;
  border:1px solid #ffffffed;
  border-radius:24px;
  background:#ffffffa8;
  box-shadow:0 12px 45px #20371d24,0 2px 5px #ffffff80 inset;
  backdrop-filter:blur(24px) saturate(150%);
  -webkit-backdrop-filter:blur(24px) saturate(150%);
}
.cmap-dock button{display:flex;align-items:center;justify-content:center;gap:9px;position:relative;z-index:1;height:46px;background:none;border-radius:18px;font-size:13px;font-weight:550;color:#63715c;transition:color .2s}
.cmap-dock button[aria-pressed=true]{color:var(--lime)}
.cmap-dock-active{position:absolute;top:6px;bottom:6px;left:6px;width:112px;background:var(--ink);border-radius:18px;box-shadow:0 3px 12px #182d201b;transition:transform .22s cubic-bezier(.2,.8,.2,1)}
.cmap-dock-active.is-feed{transform:translateX(112px)}

.cmap-empty{position:absolute;z-index:600;top:52%;left:50%;transform:translate(-50%,-40%);border-radius:24px;padding:26px;text-align:center;max-width:350px;width:calc(100% - 36px);display:flex;flex-wrap:wrap;gap:12px;justify-content:center}
.cmap-empty>svg{width:100%;color:#8b9c75}
.cmap-empty h2{font-size:20px;letter-spacing:-.6px;font-weight:550;width:100%}
.cmap-empty p{font-size:12px;color:var(--muted);width:100%}
.cmap-alert{position:absolute;bottom:106px;left:50%;transform:translateX(-50%);z-index:1000;display:flex;align-items:center;gap:15px;padding:13px 17px;border-radius:15px;max-width:calc(100% - 28px);width:max-content;font-size:12px}
.cmap-alert>span{overflow-wrap:anywhere}
.cmap-alert button{flex:none;background:none;text-decoration:underline}
.cmap-toast{position:absolute;z-index:10020;bottom:110px;left:50%;transform:translateX(-50%);background:var(--ink);color:white;padding:12px 19px;border-radius:14px;box-shadow:0 7px 30px #1f321a30;font-size:12px}

.cmap-shade{position:absolute;inset:0;z-index:10000;background:#17251f96;backdrop-filter:blur(9px);display:flex;align-items:center;justify-content:center;padding:28px}
.cmap-dialog{display:flex;flex-direction:column;max-height:100%;width:min(940px,100%);background:#f5f6ef;border-radius:28px;overflow:hidden;box-shadow:0 25px 100px #12221955;animation:cmap-rise .2s ease}
.cmap-dialog-head{position:relative;padding:25px 29px 23px;background:white;flex:none;border-bottom:1px solid var(--line)}
.cmap-dialog-head h2{font-size:32px;font-weight:560;letter-spacing:-1px;line-height:1.13;padding-right:40px;overflow-wrap:anywhere}
.cmap-address{font-size:12px;color:var(--muted);margin-top:8px!important;padding-right:25px}
.cmap-dialog-close{position:absolute;top:19px;right:19px;width:36px;height:36px;border-radius:12px}
.cmap-dialog-actions{display:flex;align-items:center;gap:8px;margin-top:17px;flex-wrap:wrap}
.cmap-dialog-actions>span{font-size:11px;color:var(--muted);margin-left:auto}
.cmap-gallery{padding:24px;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:26px;overflow:auto;min-height:0;overscroll-behavior:contain;align-items:start}
.cmap-gallery>article:only-child{grid-column:1/-1;max-width:500px;justify-self:center;width:100%}
.cmap-gallery .cmap-byline{margin-bottom:10px}
.cmap-gallery .cmap-media{aspect-ratio:4/5;border-radius:18px;max-height:580px}
.cmap-gallery .cmap-media img,.cmap-gallery .cmap-media video{object-fit:contain}
.cmap-original{display:inline-flex;align-items:center;gap:5px;font-size:10px;color:#8a9580!important;margin-top:9px}

@keyframes cmap-spin{
  to{transform:rotate(360deg)}
}
@keyframes cmap-shimmer{
  to{background-position:-220% 0}
}
@keyframes cmap-rise{
  from{opacity:0;transform:translateY(8px)}
  to{opacity:1;transform:translateY(0)}
}

@media(max-width:1100px){
  .cmap-header{grid-template-columns:150px minmax(0,1fr) 44px;gap:12px;left:20px;right:20px}
  .cmap-brand{font-size:25px;padding:12px 15px}
  .cmap-filters select{max-width:142px;font-size:11px}
  .cmap-near{padding:11px 9px;font-size:11px!important}
  .cmap-controls{padding-left:12px}
  .cmap-feed-grid{gap:18px}
  .cmap-card-body{padding:15px}
  .cmap-card h2{font-size:21px}
  .cmap-visit{padding:7px;font-size:9px}
  .cmap-card-actions{gap:6px}
}

@media(max-width:900px) and (min-width:761px){
  .cmap-feed-grid{grid-template-columns:repeat(2,minmax(0,1fr))}
  .cmap-search input{font-size:11px}
  .cmap-search{min-width:60px}
  .cmap-filters select{max-width:115px}
  .cmap-filters .cmap-radius{max-width:75px}
  .cmap-near{gap:5px}
  .cmap-header{grid-template-columns:135px minmax(0,1fr) 40px;gap:9px}
  .cmap-brand{font-size:23px}
  .cmap-share{width:40px;height:40px}
}

@media(max-width:760px){
  .cmap-root{--head:180px}
  .cmap-header{top:12px;left:12px;right:12px;grid-template-columns:minmax(0,1fr) 42px;gap:9px}
  .cmap-brand{grid-row:1;grid-column:1;width:fit-content;font-size:25px;padding:10px 14px;border-radius:17px;display:flex;align-items:center;gap:0}
  .cmap-brand>span{font-size:6px;letter-spacing:1.1px;max-width:48px;line-height:1.6;margin:0 0 0 12px;padding-left:12px;border-left:1px solid #dfe5d7}
  .cmap-brand sup{align-self:flex-start;font-size:8px;line-height:15px}
  .cmap-share{grid-row:1;grid-column:2;width:42px;height:42px;border-radius:15px}
  .cmap-controls{grid-row:2;grid-column:1/-1;display:block;padding:0 11px;border-radius:18px;min-height:0}
  .cmap-search{height:43px;border-bottom:1px solid #e3e8dc;gap:8px}
  .cmap-search input{padding:9px 2px;font-size:12px}
  .cmap-filters{height:43px;gap:5px}
  .cmap-filters select{border:0;flex:1;max-width:none;min-width:0;font-size:11px;padding:7px 0}
  .cmap-filters .cmap-radius{max-width:98px;flex:0 1 98px;padding-left:6px}
  .cmap-near{padding:7px 9px;border-radius:10px;font-size:11px!important}
  .cmap-near svg{width:15px}
  .cmap-summary{top:var(--head);left:12px;padding:10px 13px;border-radius:15px;max-width:calc(100% - 79px)}
  .cmap-summary .cmap-eyebrow,.cmap-summary h1{display:none}
  .cmap-summary-count{margin:0!important;font-size:11px}
  .cmap-summary .cmap-reset{font-size:9px!important;margin-top:4px}
  .cmap-fit{top:var(--head);right:12px;width:37px;height:37px;border-radius:13px}
  .cmap-map-hint{display:none}
  .cmap-root .leaflet-control-zoom{margin-right:13px;margin-bottom:22px}
  .cmap-root .leaflet-bar a{width:34px;height:34px;line-height:34px}
  .cmap-root .leaflet-control-attribution{font-size:8px}
  .cmap-peek{display:none}
  .cmap-dock{bottom:max(25px,env(safe-area-inset-bottom));grid-template-columns:repeat(2,104px);border-radius:22px;padding:5px}
  .cmap-dock button{height:44px;font-size:12px;border-radius:17px;gap:8px}
  .cmap-dock-active{top:5px;bottom:5px;left:5px;width:104px;border-radius:17px}
  .cmap-dock-active.is-feed{transform:translateX(104px)}
  .cmap-feed{padding:calc(var(--head) + 13px) 15px 125px}
  .cmap-feed-title{display:block;margin-bottom:23px}
  .cmap-feed-title h1{font-size:30px;letter-spacing:-1.2px}
  .cmap-feed-title .cmap-eyebrow{font-size:7px;margin-bottom:7px!important}
  .cmap-feed-title p:not(.cmap-eyebrow){font-size:11px;line-height:1.5;margin-top:8px}
  .cmap-feed-title>span{display:block;font-size:10px;margin-top:13px}
  .cmap-feed-grid{grid-template-columns:minmax(0,1fr);gap:21px;max-width:500px;margin:0 auto}
  .cmap-card{border-radius:23px}
  .cmap-card-body{padding:16px 18px}
  .cmap-card h2{font-size:24px}
  .cmap-card-actions{gap:11px}
  .cmap-visit{font-size:10px;padding:8px 11px}
  .cmap-media{aspect-ratio:4/3}
  .cmap-empty{top:53%;padding:23px;max-width:320px}
  .cmap-empty h2{font-size:18px}
  .cmap-alert{font-size:11px;bottom:100px;gap:10px;padding:12px 14px}
  .cmap-map-error{font-size:11px;bottom:100px;width:calc(100% - 28px)}
  .cmap-shade{padding:9px;background:#17251fd1}
  .cmap-dialog{border-radius:22px;height:100%;max-height:100%}
  .cmap-dialog-head{padding:21px 19px 18px}
  .cmap-dialog-head h2{font-size:27px;padding-right:35px}
  .cmap-dialog-close{right:13px;top:15px}
  .cmap-address{font-size:11px}
  .cmap-dialog-actions{gap:6px;margin-top:14px}
  .cmap-dialog-actions .cmap-button{font-size:11px!important;padding:9px 11px;min-height:37px}
  .cmap-dialog-actions>span{font-size:10px}
  .cmap-gallery{grid-template-columns:minmax(0,1fr);padding:18px;gap:25px}
  .cmap-gallery .cmap-media{max-height:68dvh}
  .cmap-gallery>article:only-child{max-width:none}
  .cmap-toast{bottom:100px;white-space:nowrap;font-size:11px}
}

@media(min-width:541px) and (max-width:760px){
  .cmap-feed-grid{grid-template-columns:repeat(2,minmax(0,1fr));max-width:none;gap:15px}
  .cmap-card-body{padding:12px}
  .cmap-card h2{font-size:20px}
  .cmap-card-actions{gap:6px}
  .cmap-visit{font-size:9px;padding:7px}
  .cmap-pin-button{display:none}
}

@media(hover:none){
  .cmap-desktop{display:none}
  .cmap-touch{display:inline}
  .cmap-card:hover{transform:none}
}

@media(prefers-reduced-motion:reduce){
  .cmap-root *{
    animation:none!important;
    transition:none!important;
    scroll-behavior:auto!important;
  }
}
`;